import type { ReactNode } from "react";

import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import type { ColumnDef } from "@tanstack/react-table";
import { RotateCcwIcon, TriangleAlertIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const SKELETON_ROWS = ["r1", "r2", "r3", "r4", "r5"];

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  /**
   * So'rov yiqilganmi. Frontend versiyasida bu prop YO'Q edi: xato bo'lganda
   * jadval "hech narsa topilmadi" deb ko'rsatardi va odam bo'sh ro'yxat bilan
   * yiqilgan so'rovni farqlay olmasdi.
   */
  isError?: boolean;
  /** `isError` bilan birga: qayta urinish tugmasi. */
  onRetry?: () => void;
  /** Oddiy matnli bo'sh holat. */
  emptyMessage: string;
  /** Boyroq bo'sh holat (masalan `<EmptyState>` + "qo'shish" tugmasi). */
  empty?: ReactNode;
  /** Berilsa, har bir qator bosiladigan (va klaviatura bilan ochiladigan) bo'ladi. */
  onRowClick?: (row: TData) => void;
  /** Bosiladigan qator uchun skrinrider matni ("… ni ochish"). */
  rowLabel?: (row: TData) => string;
}

/**
 * Server tomonidan boshqariladigan jadval. Sahifalash/saralash/filtrlash
 * tashqarida (`useUrlFilters` + API), shuning uchun bu komponent faqat joriy
 * sahifadagi qatorlarni chizadi. Pastki qismi uchun <TablePagination /> bilan
 * juftlashtiring.
 */
export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading,
  isError,
  onRetry,
  emptyMessage,
  empty,
  onRowClick,
  rowLabel,
}: DataTableProps<TData, TValue>) {
  const { t } = useTranslation();

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const rows = table.getRowModel().rows;

  const renderBody = () => {
    if (isLoading) {
      return SKELETON_ROWS.map(rowKey => (
        <TableRow key={rowKey}>
          {columns.map((_, index) => (
            <TableCell key={`${rowKey}-${index}`}>
              <Skeleton className="h-5 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ));
    }

    if (isError) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={columns.length} className="h-40">
            <div className="flex flex-col items-center justify-center gap-3 text-center">
              <span className="bg-destructive/10 text-destructive grid size-12 place-items-center rounded-2xl">
                <TriangleAlertIcon className="size-6" />
              </span>
              <div>
                <p className="text-foreground text-sm font-semibold">{t("COMMON.LOAD_FAILED")}</p>
                <p className="text-muted-foreground mt-1 text-sm">{t("COMMON.LOAD_FAILED_HINT")}</p>
              </div>
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  <RotateCcwIcon className="size-4" />
                  {t("COMMON.RETRY")}
                </Button>
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    }

    if (rows.length === 0) {
      return (
        <TableRow className="hover:bg-transparent">
          <TableCell
            colSpan={columns.length}
            className="text-muted-foreground h-40 text-center text-sm"
          >
            {empty ?? emptyMessage}
          </TableCell>
        </TableRow>
      );
    }

    return rows.map(row => (
      <TableRow
        key={row.id}
        // Bosiladigan qator KLAVIATURA bilan ham ochilishi shart. Ilgari bu
        // yerda faqat `onClick` bor edi: sichqonchasiz odam jadvaldagi hech
        // bir yozuvni ocha olmasdi va skrinrider qatorni "bosiladigan" deb
        // umuman e'lon qilmasdi.
        onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        onKeyDown={
          onRowClick
            ? event => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                onRowClick(row.original);
              }
            : undefined
        }
        tabIndex={onRowClick ? 0 : undefined}
        role={onRowClick ? "button" : undefined}
        aria-label={onRowClick ? rowLabel?.(row.original) : undefined}
        className={
          onRowClick
            ? "focus-visible:ring-ring cursor-pointer focus-visible:ring-2 focus-visible:outline-none"
            : undefined
        }
      >
        {row.getVisibleCells().map(cell => (
          <TableCell key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  return (
    <div className="border-border bg-card overflow-x-auto rounded-xl border shadow-sm">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id} className="bg-muted/40 hover:bg-muted/40">
              {headerGroup.headers.map(header => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>{renderBody()}</TableBody>
      </Table>
    </div>
  );
}
